import { createFileRoute } from "@tanstack/react-router"
import { Save } from "lucide-react"
import { useEffect, useState } from "react"

import type { BirthdayConfigWire } from "@/lib/birthdays-api"

import { SectionKicker } from "@/components/shared/section-kicker"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LabeledSelect } from "@/components/ui/labeled-select"
import { FormPageSkeleton } from "@/components/ui/page-skeletons"
import { Switch } from "@/components/ui/switch"
import {
  useBirthdayConfigQuery,
  useUpdateBirthdayConfigMutation,
} from "@/hooks/birthdays"

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${String(h).padStart(2, "0")}:00`,
}))

export const Route = createFileRoute("/_authed/_admin/birthdays/")({
  component: BirthdaysConfigPage,
})

type ConfigDraft = {
  enabled: boolean
  announcementChannelId: string
  birthdayRoleId: string
  announcementHour: number
  pingCelebrant: boolean
}

function BirthdaysConfigPage() {
  const configQuery = useBirthdayConfigQuery()
  const updateMutation = useUpdateBirthdayConfigMutation()
  const [draft, setDraft] = useState<ConfigDraft | null>(null)

  useEffect(() => {
    if (configQuery.data && draft === null) {
      const c = configQuery.data
      setDraft({
        enabled: c.enabled,
        announcementChannelId: c.announcementChannelId ?? "",
        birthdayRoleId: c.birthdayRoleId ?? "",
        announcementHour: c.announcementHour,
        pingCelebrant: c.pingCelebrant,
      })
    }
  }, [configQuery.data, draft])

  if (configQuery.isLoading) {
    return <FormPageSkeleton />
  }

  if (configQuery.error || !draft) {
    return (
      <Card>
        <CardContent>
          <Alert>
            <AlertDescription>
              {configQuery.error?.message ??
                "Unable to load birthday settings."}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  const updateField = <K extends keyof ConfigDraft>(
    key: K,
    value: ConfigDraft[K],
  ) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  const handleSave = () => {
    if (!draft) {
      return
    }
    updateMutation.mutate({
      enabled: draft.enabled,
      announcementChannelId: draft.announcementChannelId || null,
      birthdayRoleId: draft.birthdayRoleId || null,
      announcementHour: draft.announcementHour,
      pingCelebrant: draft.pingCelebrant,
    })
  }

  return (
    <div className="grid gap-5 md:gap-6">
      <SectionKicker icon={<Save className="size-3.5" />}>
        Birthday announcement & role configuration
      </SectionKicker>

      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="bd-enabled">Enabled</Label>
              <p className="text-sm text-muted-foreground">
                Master switch for the daily birthday check and announcements.
              </p>
            </div>
            <Switch
              id="bd-enabled"
              checked={draft.enabled}
              onCheckedChange={(checked) => updateField("enabled", checked)}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="bd-ping">Ping celebrant</Label>
              <p className="text-sm text-muted-foreground">
                Prepend a mention to each birthday message.
              </p>
            </div>
            <Switch
              id="bd-ping"
              checked={draft.pingCelebrant}
              onCheckedChange={(checked) =>
                updateField("pingCelebrant", checked)
              }
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="bd-hour">Announcement hour</Label>
            <LabeledSelect
              value={String(draft.announcementHour)}
              onValueChange={(v) => updateField("announcementHour", Number(v))}
              options={HOUR_OPTIONS}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground">
              Earliest hour (guild-local) at which announcements are sent.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Discord</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="grid gap-2">
            <Label htmlFor="bd-channel">Announcement channel ID</Label>
            <Input
              id="bd-channel"
              value={draft.announcementChannelId}
              onChange={(e) =>
                updateField("announcementChannelId", e.target.value.trim())
              }
              placeholder="Channel snowflake (leave empty to disable)"
            />
            <p className="text-sm text-muted-foreground">
              Validated against the live guild on save.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="bd-role">Birthday role ID</Label>
            <Input
              id="bd-role"
              value={draft.birthdayRoleId}
              onChange={(e) =>
                updateField("birthdayRoleId", e.target.value.trim())
              }
              placeholder="Role snowflake (leave empty to disable)"
            />
            <p className="text-sm text-muted-foreground">
              Validated on save. Changing the role strips the old role from all
              current holders.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          <Save className="size-4" />
          {updateMutation.isPending ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  )
}
